import { Inject, Injectable } from '@angular/core';
import { HttpParams, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { EnumInjectionTokens } from '../constants';
import { IEnvironment } from '../interfaces';

export interface RequestOptionsArgs {
  body?: any;
  headers?: HttpHeaders;
  observe?: 'body';
  params?: HttpParams | {
    [param: string]: string | string[];
  };
  reportProgress?: boolean;
  responseType?: ('arraybuffer' | 'blob' | 'json' | 'text'),
  withCredentials?: boolean;
}

@Injectable({ providedIn: 'root' })
export class GemsHttpService {
  static applyCustomHeaders(url: string, options?: RequestOptionsArgs): any {
    const requestOptions: RequestOptionsArgs = options
      ? options
      : {};
    // NOTE: if custom options are provided, this ensures the caller is not required
    // to create empty "headers" property if not explicitly needed in the caller
    if (!requestOptions.headers) requestOptions.headers = new HttpHeaders();

    return requestOptions;
  }

  constructor(
    private http: HttpClient,
    @Inject(EnumInjectionTokens.Environment) private environment: IEnvironment
  ) { }

  request(method: string, url: string, options?: RequestOptionsArgs) {
    const customOptions = GemsHttpService.applyCustomHeaders(url, options);
    return this.http.request(method, url, customOptions);
  }

  get<T>(url: string, options?: RequestOptionsArgs): Observable<any> {
    let cacheBustedUrl = url.replace(/[?|&]cacheBuster=\d+/, '');
    cacheBustedUrl += cacheBustedUrl.indexOf('?') === -1 ? '?' : '&';
    cacheBustedUrl += 'cacheBuster=' + new Date().getTime();
    const customOptions = GemsHttpService.applyCustomHeaders(cacheBustedUrl, options);
    return this.http.get<T>(cacheBustedUrl, customOptions);
  }

  // NOTE: returning 'Observable<ArrayBuffer>' here without passing any params to the 'http.get' call
  // seems to keep both the IDE and compiler happy, as well as ensuring that the code continues to function;
  // if consumer of this operation requires different return type than 'Observable<ArrayBuffer>',
  // then an alternate overload will need to be provided
  // [Patterson, John - 2019.04.15.15.11]
  getWithResponse(url: string, options?: RequestOptionsArgs): Observable<ArrayBuffer> {
    // RESOURCE: https://www.dotnetcurry.com/angularjs/1448/angular-http-client-interceptors-headers-event
    let cacheBustedUrl = url.replace(/[?|&]cacheBuster=\d+/, '');
    cacheBustedUrl += cacheBustedUrl.indexOf('?') === -1 ? '?' : '&';
    cacheBustedUrl += 'cacheBuster=' + new Date().getTime();
    const customOptions = GemsHttpService.applyCustomHeaders(cacheBustedUrl, options);
    customOptions.observe = 'response';
    return this.http.get(cacheBustedUrl, customOptions);
  }

  post(url: string, body: any, options?: RequestOptionsArgs): Observable<any> {
    const customOptions = GemsHttpService.applyCustomHeaders(url, options);
    return this.http.post(url, body, customOptions);
  }

  delete(url: string, options?: RequestOptionsArgs): Observable<any> {
    const customOptions = GemsHttpService.applyCustomHeaders(url, options);
    return this.http.delete(url, customOptions);
  }
}
